/**
 * Add组件 - 用于上传视频和生成舞蹈项目
 * @param {Object} props - 组件属性
 * @param {Function} props.onGenerate - 生成舞蹈项目后的回调函数
 */
import React, { useEffect, useRef, useState } from "react";
import UploadIcon from "../img/上传.svg";
import api from "../api";

import { Cell,Toast } from 'react-vant'
import { useTranslation } from 'react-i18next';
// 在文件顶部导入默认图片
import defaultCoverImage from '../img/default-graph.png'
import { PAYMENT_STATUS } from "../paymentStatus";

// 支付流程先隐藏：点击“立即生成”直接走基础生成
const ENABLE_PAYMENT_FLOW = false;

// 计算总字符权重（例如：汉字=2，英文字母=1）
function countWeightedCharacters(str) {
  let count = 0;
  for (let char of str) {
    if (/[\u4e00-\u9fa5]/.test(char)) {
      count += 2;  // 汉字算2个字符
    } else {
      count += 1;  // 其他字符算1个
    }
  }
  return count;
}

// 使用api.js中配置的baseURL
export default function Add({ onGenerate, repayOrder, onRepayExit, onOpenAcornDialog }) {
  const { t } = useTranslation();
//文件输入框状态
  const fileInputRef = useRef(null);

  const isRepayMode = !!repayOrder;
  const uploadInstructions = [
    t('createProject.instruction1'),
    t('createProject.instruction2'),
    t('createProject.instruction3'),
    t('createProject.instruction4'),
    t('createProject.instruction5'),
  ];

  // 状态：存储视频名称
  var [videoName, setVideoName] = useState("");
  // 状态：存储机器人型号
  var [robotModel, setRobotModel] = useState("booster_k1");

  // 状态：存储错误信息
  var [error, setError] = useState("");
  // 状态：存储上传的文件信息
  var [uploadedFile, setUploadedFile] = useState(null);
  // 状态：存储文件预览URL（视频封面或图片）
  var [previewUrl, setPreviewUrl] = useState(null);
  // 状态：存储上传URL
  var [uploadUrl, setUploadUrl] = useState(null);
  // 状态：存储封面上传URL
  var [uploadCoverUrl, setUploadCoverUrl] = useState(null);
  // 状态：存储媒体ID
  var [mediaId, setMediaId] = useState(null);
  // 状态：存储上传状态
  var [uploading, setUploading] = useState(false);
  // 状态：存储上传进度
  var [uploadProgress, setUploadProgress] = useState(0);
  // 状态：标记视频是否已真正上传成功
  var [uploadSuccess, setUploadSuccess] = useState(false);
  // 状态：视频时长（秒）
  var [videoDuration, setVideoDuration] = useState(null);
  // 状态：商品列表第一个商品（listProduct 返回，暂不展示，供后续报价使用）
  var [firstProduct, setFirstProduct] = useState(null);
  // 状态：橡树果可用余额
  var [acornBalance, setAcornBalance] = useState(null);
  // 状态：本次将消耗的橡树果数量（getProductQuote 返回）
  var [consumeAmount, setConsumeAmount] = useState(null);

  // 状态：控制"立即生成"确认弹框
  var [showGenerateDialog, setShowGenerateDialog] = useState(false);
  // 状态：控制"生成舞蹈"确认弹框（点击立即生成后弹出，选择生成方式）
  var [showGenerateConfirmDialog, setShowGenerateConfirmDialog] = useState(false);
  // 状态：生成方式，默认"full"（完整舞蹈，包含仿真+模型）
  var [generateType, setGenerateType] = useState("full");
  // 状态：控制 iOS 提示弹框
  var [showIosTipDialog, setShowIosTipDialog] = useState(false);
  // 状态：支付方式（仅用于切换高亮展示）
  var [payMethod, setPayMethod] = useState("alipay"); // wechat | alipay | unionpay

  // ✅ 新增：支付状态
  var [paymentStatus, setPaymentStatus] = useState(PAYMENT_STATUS.INIT);
  var [payErrorMsg, setPayErrorMsg] = useState("");
  var [orderInfo, setOrderInfo] = useState({
    id: `ORDER_${Date.now()}`,
    amount: 100.00,
    title: "舞蹈生成服务",
    productId: "",
    productDesc: "",
    payUrl: "",
    paymentOutId: "",
    orderString: "",
    paymentFormHtml: "",
  });

  const videoNameRef = useRef(null);
  const paymentFormRef = useRef(null);
  const pollingTimerRef = useRef(null);
  const pollingCountRef = useRef(0);
  const isPollingRef = useRef(false);

  const clearPaymentPolling = () => {
    if (pollingTimerRef.current) {
      window.clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    pollingCountRef.current = 0;
    isPollingRef.current = false;
  };

  useEffect(() => {
    return () => {
      clearPaymentPolling();
    };
  }, []);

  useEffect(() => {
    if (!repayOrder) {
      return;
    }
    setOrderInfo((prev) => ({
      ...prev,
      amount: Number(repayOrder?.amount ?? prev.amount) || prev.amount,
      title: repayOrder?.productName || repayOrder?.danceName || prev.title,
      productId: String(repayOrder?.productId || prev.productId || ""),
      productDesc: repayOrder?.orderNo || prev.productDesc,
      paymentOutId: String(repayOrder?.paymentOutId || ""),
      orderString: "",
      paymentFormHtml: "",
    }));
    setPayErrorMsg("");
    setPaymentStatus(PAYMENT_STATUS.CONFIRMING);
    setShowGenerateDialog(true);
  }, [repayOrder]);

  const queryPaymentResult = async (paymentOutId) => {
    if (!paymentOutId) {
      return null;
    }
    return await api.login.get(`/payment/query/${paymentOutId}`);
  };

  const isPaySuccess = (queryResp) => {
    const status = String(
      queryResp?.payStatus ||
        queryResp?.status ||
        queryResp?.paymentStatus ||
        queryResp?.data?.payStatus ||
        queryResp?.data?.status ||
        "",
    ).toUpperCase();

    return (
      queryResp?.paid === true ||
      status === "SUCCESS" ||
      status === "PAID" ||
      status === "COMPLETED"
    );
  };

  const isPayFailed = (queryResp) => {
    const status = String(
      queryResp?.payStatus ||
        queryResp?.status ||
        queryResp?.paymentStatus ||
        queryResp?.data?.payStatus ||
        queryResp?.data?.status ||
        "",
    ).toUpperCase();
    return status === "FAIL" || status === "FAILED" || status === "CLOSED";
  };

  const submitPaymentForm = (html) => {
    if (!html || !paymentFormRef.current) {
      return false;
    }

    paymentFormRef.current.innerHTML = html;
    const formEl = paymentFormRef.current.querySelector("form");
    if (!formEl) {
      return false;
    }

    formEl.submit();
    return true;
  };

  const normalizePayResponse = (payResp) => {
    const lvl1 = payResp || {};
    const lvl2 = lvl1?.data || {};
    const lvl3 = lvl2?.data || {};

    const paymentOutId = String(
      lvl1?.paymentOutId || lvl2?.paymentOutId || lvl3?.paymentOutId || "",
    );

    const orderString = String(
      lvl1?.orderString || lvl2?.orderString || lvl3?.orderString || "",
    );

    const rawFormHtml = String(
      lvl1?.formHtml || lvl2?.formHtml || lvl3?.formHtml || "",
    );

    const formHtml =
      rawFormHtml ||
      (orderString.includes("<form") ? orderString : "");

    return {
      paymentOutId,
      orderString,
      formHtml,
    };
  };

  const formatPayErrorMessage = (rawMsg) => {
    const msg = String(rawMsg || "").trim();
    if (!msg) {
      return t('payment.requestFailed');
    }

    const lowerMsg = msg.toLowerCase();
    if (
      lowerMsg.includes("error updating database") ||
      lowerMsg.includes("psqlexception")
    ) {
      if (lowerMsg.includes('column "extra"') && lowerMsg.includes("jsonb")) {
        return t('payment.configError');
      }
      return t('payment.dbError');
    }

    return msg.length > 120 ? `${msg.slice(0, 120)}...` : msg;
  };

  const isPayApiSuccessCode = (retCode) => {
    const code = String(retCode ?? "");
    return (
      code === "000000"
    );
  };

  const startPaymentPolling = (paymentOutId) => {
    if (!paymentOutId || isPollingRef.current) {
      return;
    }

    isPollingRef.current = true;
    pollingCountRef.current = 0;

    pollingTimerRef.current = window.setInterval(async () => {
      try {
        pollingCountRef.current += 1;
        const queryResp = await queryPaymentResult(paymentOutId);
        if(queryResp.status !=2){
            console.log("[payment:query] queryResp:", queryResp.status);
            return
        }
      
          //todo 支付成功了
 
      } catch (error) {
        if (pollingCountRef.current >= 60) {
          clearPaymentPolling();
          setPaymentStatus(PAYMENT_STATUS.FAIL);
          setPayErrorMsg(t('payment.queryFailed'));
        }
      }
    }, 3000);
  };
  // ✅ 修复：支付成功后执行的生成逻辑
  var handleGenerateAfterPayment = async () => {
    console.log("支付成功，开始执行生成逻辑...");
    
    // 验证视频名称是否为空
    if (!videoName.trim()) {
      setError(t('createProject.errorNameRequired'));
      return;
    }
    // 验证是否选择了文件
    if (!uploadedFile) {
      setError(t('createProject.errorFileRequired'));
      return;
    }
    // 清除错误信息
    setError("");

    try {
      // 设置上传状态
      setUploading(true);
      setUploadProgress(0);

      // 创建任务
      setUploadProgress(95);

      const robotModel = "booster_k1";
      console.log(
        "creatTask:start " +
          JSON.stringify({
            mediaId: mediaId,
            task_name: videoName,
            robot_mode: robotModel,
          }),
      );
      const taskData = await creatTask({
        mediaId: mediaId,
        task_name: videoName,
        robot_mode: robotModel,
      });
      console.log("creatTask:success " + JSON.stringify(taskData));

      setUploadProgress(100);

      // 调用回调函数，将新的舞蹈项目信息传递给父组件
      if (onGenerate) {
        onGenerate({
          id: Date.now(), // 生成唯一ID
          name: videoName,
          model: "booster_k1", // 默认型号
          coverUrl: previewUrl,
          fileName: uploadedFile ? uploadedFile.name : "",
          fileType: uploadedFile ? uploadedFile.type : "",
          mediaId: mediaId,
          taskId: taskData ? taskData.task_id : null,
          status: taskData ? taskData.status : "生成中",
          progress: taskData ? taskData.progress : 0,
        });
      }

      // 重置状态
      setUploading(false);
      setUploadProgress(0);
      setUploadedFile(null);
      setPreviewUrl(null);
      setUploadUrl(null);
      setUploadCoverUrl(null);
      setMediaId(null);
      setUploadSuccess(false);
      setVideoName("");
    } catch (error) {
      console.error("handleGenerateClick:failed " + error.message);
      setError(t('createProject.errorUploadFailed'));
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ✅ 修复：支付处理函数
  var handlePaymentProcess = async () => {
    try {
      if (!orderInfo?.productId) {
        setPayErrorMsg(t('payment.productMissing'));
        setPaymentStatus(PAYMENT_STATUS.FAIL);
        return;
      }
      if (!payMethod) {
        setPayErrorMsg(t('payment.methodMissing'));
        setPaymentStatus(PAYMENT_STATUS.FAIL);
        return;
      }

      clearPaymentPolling();
      setPaymentStatus(PAYMENT_STATUS.PROCESSING);
      setPayErrorMsg("");

      const payResp = await applyPayment({
        productId: orderInfo.productId,
        payMethod: payMethod,
      });

      // const { paymentOutId, orderString, formHtml } = normalizePayResponse(payResp);

      const payableHtml = payResp.orderString || "";
      const paymentOutId = payResp.paymentOutId || "";
      console.log("[payment:pay] payableHtml:", payableHtml);

      if (!paymentOutId) {
        setPaymentStatus(PAYMENT_STATUS.FAIL);
        setPayErrorMsg(t('payment.orderNoMissing'));
        return;
      }
      openAlipayForm(payableHtml)
      // setOrderInfo((prev) => ({
      //   ...prev,
      //   paymentOutId,
      //   orderString,
      //   paymentFormHtml: payableHtml,
      // }));

      // if (!submitPaymentForm(payableHtml)) {
      //   setPaymentStatus(PAYMENT_STATUS.FAIL);
      //   setPayErrorMsg("支付表单异常，请重试");
      //   return;
      // }

      setPayErrorMsg(t('payment.processingMsg'));
      // startPaymentPolling(paymentOutId);
    } catch (err) {
      setPaymentStatus(PAYMENT_STATUS.FAIL);
      setPayErrorMsg(t('payment.processFailed'));
    }
  };
  const openAlipayForm = (formHtml) => {
    // const formHtml = `...你的表单HTML...`; // 同上

    // 创建 Blob
    const blob = new Blob([formHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    // 打开新窗口
    const newWindow = window.open(url, '_blank');
    
    // 清理 URL（可选，在窗口关闭后清理）
    if (newWindow) {
      newWindow.onload = () => {
        URL.revokeObjectURL(url);
      };
    }
  };

  // ✅ 新增：取消支付
  var handleCancelPayment = () => {
    clearPaymentPolling();
    setShowGenerateDialog(false);
    setPaymentStatus(PAYMENT_STATUS.INIT);
    setPayErrorMsg("");
    if (isRepayMode && typeof onRepayExit === "function") {
      onRepayExit();
    }
  };

  // 获取商品列表（当前固定 productListType = 2）
  var fetchProductList = async () => {
    const response = await api.login.get("/payment/list/product", {
      offset: 0,
      limit: 20,
      productListType: 2,
    });

    // 详细打印：后端可能把 products 放在不同层级
    console.log("[payment:list] raw response:", response);
    console.log("[payment:list] response.data:", response?.data);
    console.log("[payment:list] response.data.data:", response?.data?.data);

    const products = response.products || [];
    console.log(
      "[payment:list] normalized products length:",
      products.length,
    );
    return products;
  };

  // 查询商品详情（不同环境可能存在不同路由命名，做兼容兜底）
  var fetchProductDetail = async (productId) => {
    if (!productId) return null;
    console.log("[payment:detail] request productId:", productId);
    const detailResponse = await api.login.get(`/payment/query/product/${productId}`);
    console.log("[payment:detail] response:", detailResponse);
    return detailResponse;
  };

  // 申请支付
  var applyPayment = async ({ productId, payMethod }) => {
    if (!productId) {
      throw new Error("productId is required");
    }

    const payChannelMap = {
      alipay: 0,
      wechat: 1,
      unionpay: 2,
    };
    const paymentMethod = payChannelMap[payMethod];
    const paymentOrigin = 1;

    if (paymentMethod === undefined || paymentMethod === null) {
      throw new Error("paymentMethod is required");
    }
    if (paymentOrigin === undefined || paymentOrigin === null) {
      throw new Error("paymentOrigin is required");
    }

    const basePayload = {
      productId,
      paymentMethod,
      paymentOrigin,
    };

    console.log("[payment:pay] payload:", basePayload);
    let payResp = await api.login.post("/payment/pay", basePayload);
    console.log("[payment:pay] response:", payResp);

    return payResp;
  };

  // 查询橡树果余额
  var fetchAcornBalance = async () => {
    try {
      const res = await api.getAcornBalance();
      const raw = res?.availableAmount;
      // 只有拿到有效数字才算数；空值/异常值按"未知(null)"处理，避免和真实余额 0 混淆
      const parsed = raw == null ? NaN : Number(raw);
      setAcornBalance(Number.isFinite(parsed) ? parsed : null);
    } catch (error) {
      console.error("fetchAcornBalance:failed", error);
      // 拉取失败保持 null（未知），不误判为余额 0
      setAcornBalance(null);
    }
  };

  // ✅ 修复：处理"立即生成"按钮点击
  var handleGenerateButtonClick = async () => {
    // 验证视频名称是否为空
    if (!videoName.trim()) {
      setError(t('createProject.errorNameRequired'));
      return;
    }
    // 验证是否选择了文件
    if (!uploadedFile) {
      setError(t('createProject.errorFileRequired'));
      return;
    }

    // 只有视频真正上传成功后，才允许创建任务
    if (!uploadSuccess || !mediaId) {
      setError(t('createProject.errorUploadNotReady'));
      return;
    }
    setError("");

    // 拉取商品列表，保存第一个商品（暂不展示，供后续报价/生成使用）
    try {
      const res = await api.listProduct({
        page_size: 10,
        page_index: 1,
        training_entrance: "simulation",
      });
      const products = Array.isArray(res?.products) ? res.products : [];
      if (products.length) {
        const first = products[0];
        setFirstProduct(first);
        console.log("listProduct:first product", first);

        // 用第一个商品的 product_id 计算本次消耗的橡树果数量
        if (first?.product_id && mediaId) {
          const quoteRes = await api.getProductQuote({
            media_id: mediaId,
            product_id: first.product_id,
          });
          const quote = Number(quoteRes?.quote);
          setConsumeAmount(Number.isFinite(quote) ? quote : null);
          console.log("getProductQuote:quote", quote);
        }
      }
    } catch (error) {
      console.error("listProduct:failed", error);
    }

    // 查询橡树果余额
    fetchAcornBalance();

    // 弹出"生成舞蹈"确认弹框，选择生成方式后再真正生成
    setShowGenerateConfirmDialog(true);
  };

  // 生成舞蹈弹框的"确认生成"：关闭弹框后走支付流程或直接生成
  var proceedGenerate = async () => {
    // 余额不足：不关闭本弹框，在其上层复用首页的"橡树果充值"弹框引导充值
    // 取消充值后仍回到生成舞蹈弹框，由用户手动取消
    // 拿不到余额/消耗量（null）也视为不足，统一引导充值
    const insufficient =
      consumeAmount == null ||
      acornBalance == null ||
      acornBalance < consumeAmount;

    if (insufficient) {
      if (onOpenAcornDialog) onOpenAcornDialog();
      return;
    }

    setShowGenerateConfirmDialog(false);

    if (ENABLE_PAYMENT_FLOW) {
      try {
        const products = await fetchProductList();
        if (!products.length) {
          setError(t('payment.noProduct'));
          return;
        }

        const selectedProduct = products[0];
        const productId = selectedProduct?.productId || "";
        const productDetail = await fetchProductDetail(productId);
        const detailData = productDetail || {};

        setOrderInfo((prev) => ({
          ...prev,
          title:
            detailData?.productName ||
            selectedProduct?.productName ||
            prev.title,
          amount:
            Number(detailData?.price ?? selectedProduct?.price) || prev.amount,
          productId: productId || prev.productId,
          productDesc:
            detailData?.description ||
            selectedProduct?.description ||
            prev.productDesc,
          payUrl: "",
        }));
      } catch (error) {
        console.error("fetchProductList:failed " + error.message);
        setError(t('payment.getProductFailed'));
        return;
      }
      //
      setShowGenerateDialog(true);
      setPaymentStatus(PAYMENT_STATUS.CONFIRMING);
      return;
    }

    // 兜底：若关闭支付流程，直接走原有生成逻辑
    handleGenerateClick();
  };

  /**
   * 从本地视频文件提取时长（单位：秒）
   * @param {Object} file - 视频文件对象
   * @returns {Promise<number>} - 返回视频时长（秒），如果不是视频文件或获取失败则返回0
   * 说明：
   * - 后端 PATCH 接口的 `duration` 需要数字；这里直接读取文件 metadata，避免依赖后端推断。
   * - 某些浏览器/格式可能拿不到 duration（或为 NaN），此时按 0 处理，避免请求因类型问题 422。
   */
  var getMediaDurationSeconds = async (file) => {
    // 验证文件是否为视频文件
    if (!file || !file.type || !file.type.startsWith("video/")) return 0;

    // 创建Promise来获取视频时长
    return await new Promise((resolve) => {
      // 创建视频元素
      var video = document.createElement("video");
      // 创建临时URL
      var url = URL.createObjectURL(file);

      // 清理函数，用于释放临时URL
      var cleanup = () => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // 忽略清理错误
        }
      };

      // 设置视频预加载为仅 metadata
      video.preload = "metadata";
      // 设置视频源
      video.src = url;

      // 视频元数据加载完成时的回调
      video.onloadedmetadata = () => {
        // 计算视频时长，确保是有限数字，否则返回0
        var d = Number.isFinite(video.duration)
          ? Math.round(video.duration)
          : 0;
        cleanup();
        resolve(d);
      };

      // 视频加载错误时的回调
      video.onerror = () => {
        cleanup();
        resolve(0);
      };
    });
  };


  /**
   * 获取视频封面数据 (适配 iOS App/WebView)
   */
  var getVideoCoverDataUrl = async (file) => {
    console.log("=== getVideoCoverDataUrl 开始 ===");
    const supportedFormats = ['mp4', 'mov', 'avi', 'mkv', 'webm', '3gp', 'm4v'];
    const fileExt = file.name.toLowerCase().split('.').pop();
    const isSupportedFormat = supportedFormats.includes(fileExt);

    if (!file || (!file.type.startsWith("video/") && !isSupportedFormat)) {
      console.error("不支持的文件格式:", file.type, fileExt);
      // 这里建议返回一个默认图或抛出错误
      return "";
    }

    // ============ 大文件优化策略 ============
    const LARGE_FILE_THRESHOLD = 150 * 1024 * 1024; // 150MB
    const PREFERRED_HEAD_SIZE = 30 * 1024 * 1024;  // 30MB
    const isLargeFile = file.size > LARGE_FILE_THRESHOLD;

    // 注意：iOS 上 slice 有可能导致元数据丢失，如果后面加载失败，逻辑会切回全量文件
    let videoFile = isLargeFile ? file.slice(0, PREFERRED_HEAD_SIZE) : file;

    return await new Promise((resolve, reject) => {
      let url = URL.createObjectURL(videoFile);
      const video = document.createElement("video");

      // --- iOS 必须设置项 ---
      video.setAttribute("muted", "");
      video.muted = true;
      video.setAttribute("playsinline", "true");
      video.setAttribute("webkit-playsinline", "true");
      video.setAttribute("preload", "auto");
      video.style.position = "fixed";
      video.style.top = "0";
      video.style.left = "-9999px"; // 移出屏幕
      video.style.visibility = "hidden";

      // iOS 必须挂载到 DOM 才能稳定触发加载
      document.body.appendChild(video);

      let done = false;
      let tryTimes = [];
      let currentTryIndex = 0;
      const maxRetries = 4;
      let hasSwitchedToFullFile = false;

      const cleanup = () => {
        if (done) return;
        done = true;
        clearTimeout(finalTimeoutId);
        video.pause();
        if (video.parentNode) {
          document.body.removeChild(video);
        }
        URL.revokeObjectURL(url);
        video.removeAttribute("src");
        video.load(); // 强制释放内存
      };

      // 超时设置：根据文件大小动态调整
      const totalTimeout = Math.max(30000, 30000 + (file.size / 1024 / 1024) * 100);
      const finalTimeoutId = setTimeout(() => {
        console.error("最终超时 - 释放资源");
        cleanup();
        reject(new Error("封面生成超时"));
      }, totalTimeout);

      // 核心渲染函数
      const drawFrame = () => {
        try {
          if (video.videoWidth === 0 || video.videoHeight === 0) {
            trySeek();
            return;
          }

          const maxOutputDimension = 480;
          let targetWidth = video.videoWidth;
          let targetHeight = video.videoHeight;
          const ratio = Math.min(maxOutputDimension / targetWidth, maxOutputDimension / targetHeight, 1);
          targetWidth = Math.floor(targetWidth * ratio);
          targetHeight = Math.floor(targetHeight * ratio);

          const canvas = document.createElement("canvas");
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(video, 0, 0, targetWidth, targetHeight);

          // 黑帧检测逻辑 (复用原逻辑)
          if (isBlackFrame(canvas)) {
            console.log(`时间点 ${tryTimes[currentTryIndex-1]}s 是黑帧，尝试下一个`);
            trySeek();
            return;
          }

          const coverUrl = canvas.toDataURL("image/jpeg", 0.7);
          console.log("封面生成成功");
          cleanup();
          resolve(coverUrl);
        } catch (err) {
          console.error("绘制出错:", err);
          trySeek();
        }
      };

      const isBlackFrame = (canvas) => {
        const ctx = canvas.getContext("2d");
        const { width, height } = canvas;
        const imageData = ctx.getImageData(0, 0, width, height).data;
        let totalLuminance = 0;
        let darkPixels = 0;
        const step = 10; // 步进采样提高性能
        let samples = 0;

        for (let i = 0; i < imageData.length; i += 4 * step) {
          const r = imageData[i], g = imageData[i + 1], b = imageData[i + 2];
          const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
          totalLuminance += luminance;
          if (luminance < 45) darkPixels++;
          samples++;
        }
        const avgLuminance = totalLuminance / samples;
        const darkRatio = darkPixels / samples;
        return avgLuminance < 35 || darkRatio > 0.85;
      };

      const trySeek = () => {
        if (currentTryIndex >= tryTimes.length) {
          if (isLargeFile && !hasSwitchedToFullFile) {
            console.log("切片加载失败或无有效帧，切换全量文件重试...");
            hasSwitchedToFullFile = true;
            URL.revokeObjectURL(url);
            url = URL.createObjectURL(file);
            currentTryIndex = 0;
            video.src = url;
            video.load();
            return;
          }
          cleanup();
          reject(new Error("无法获取有效视频帧"));
          return;
        }

        const time = tryTimes[currentTryIndex];
        currentTryIndex++;

        // iOS 必须在 pause 状态下 seek
        video.pause();
        video.currentTime = time;
      };

      // 事件监听
      video.addEventListener("loadedmetadata", () => {
        console.log("元数据加载成功，时长:", video.duration);
        const dur = video.duration;
        if (dur && Number.isFinite(dur)) {
          // 采样点：结尾、中间、开头 (避开前 0.1s 容易黑屏)
          tryTimes = [
            Math.max(0.2, dur * 0.1),
            dur * 0.5,
            Math.max(0, dur - 1.0)
          ].sort((a, b) => a - b);
        } else {
          tryTimes = [0.5, 1.5, 3.0];
        }
        trySeek();
      });

      video.addEventListener("seeked", () => {
        // seeked 触发后延迟一小会儿，确保 iOS 渲染缓冲区已更新
        setTimeout(drawFrame, 150);
      });

      video.addEventListener("error", (e) => {
        // 清理阶段（done 已置 true）触发的空 src 错误，直接忽略，避免误报
        if (done) return;
        console.error("Video Error Event:", video.error);
        if (isLargeFile && !hasSwitchedToFullFile) {
          trySeek(); // 触发切换全量文件
        } else {
          cleanup();
          reject(new Error("视频加载失败"));
        }
      });

      // 开始加载
      video.src = url;
      video.load();
    });
  };
  /**
   * 上传完成后，调用后端 `PATCH /cmtp/api/v1/media/{media_id}` 写入元数据。
   * 对应 Swagger：
   * - Path: `media_id`
   * - Header: `x-user-id`
   * - Body: `media_name`, `duration`, `status`
   */
  var updateMediaMetadata = async ({
    mediaId,
    mediaName,
    duration,
    status,
  }) => {
    if (!mediaId) {
      console.error("updateMediaMetadata:failed mediaId is required");
      return null;
    }

    try {
      const response = await api.patch(
        `/api/v1/media/${mediaId}`,
        {
          media_name: mediaName,
          duration: duration,
          status: status || "uploaded",
        },
        {},
      );
      console.log("updateMediaMetadata:apiSuccess " + JSON.stringify(response));
      return response;
    } catch (error) {
      console.error("updateMediaMetadata:apiFailed " + error.message);
      return null;
    }
  };

  /**
   * 处理视频名称输入变化
   * @param {Object} e - 事件对象
   */
  var handleVideoNameChange = (e) => {
    const newName = e.target.value;
    const weightedCount = countWeightedCharacters
      (newName);

    // 超出上限时直接阻止继续输入，不再提示错误
    if (weightedCount > 20) {
      return;
    }

    setVideoName(newName);

    // 清除错误信息
    if (error) {
      setError("");
    }
  }
  /**
   * 创建任务
   * @param {Object} params - 参数对象
   * @param {string} params.mediaId - 媒体ID
   * @param {string} params.task_name - 任务名称
   * @param {string} params.robot_mode - 机器人型号
   * @returns {Promise<Object|null>} - 返回任务创建结果或null
   * 发送POST请求到 /cmtp/api/v1/task 接口创建任务
   */
  var creatTask = async ({ mediaId, task_name, robot_mode }) => {
    // 验证媒体ID是否存在
    if (!mediaId) {
      console.error("creatTask:failed mediaId is required");
      return null;
    }
    // 发送POST请求创建任务
    try {
      const response = await api.post(
        `/api/v1/task`,
        {
          media_id: mediaId,
          task_name: task_name,
          robot_model: robot_mode,
        },
        {},
      );
      return response;
    } catch (error) {
      console.error("creatTask:apiFailed " + error.message);
      // 如果getVideoCoverDataUrl抛出了异常（而不是返回默认图片），则使用默认封面
      setPreviewUrl(defaultCoverImage);
      console.warn("视频封面生成失败，使用默认封面");

    }
  };

  /**
   * 处理立即生成按钮点击
   * 1. 验证视频名称和文件是否已选择
   * 2. 获取上传URL（如果还没有）
   * 3. 上传视频文件
   * 4. 上传封面图片
   * 5. 调用回调函数，将新的舞蹈项目信息传递给父组件
   * 6. 重置状态
   */
  var handleGenerateClick = async () => {
    // 验证视频名称是否为空
    if (!videoName.trim()) {
      setError(t('createProject.errorNameRequired'));
      return;
    }

// 验证视频名称字数限制
    // 使用加权字符数校验
    const weightedCount = countWeightedCharacters(videoName);
    if (weightedCount > 20) {
      setError(t('createProject.errorNameTooLong', { count: weightedCount }));
      return;
    }
    // 验证是否选择了文件
    if (!uploadedFile) {
      setError(t('createProject.errorFileRequired'));
      return;
    }
    // 清除错误信息
    setError("");

    try {
      // 覆盖一次后端媒体名，避免“先选文件后输入名称”时拿到 file.name
      // try {
      //   if (mediaId && uploadedFile) {
      //     await updateUrl(mediaId, videoName, uploadedFile);
      //   }
      // } catch (e) {
      //   console.warn("update media_name failed, fallback to original value:", e);
      // }

      // 设置上传状态
      setUploading(true);
      setUploadProgress(0);

      // 创建任务前再次确认视频已经上传成功
      if (!uploadSuccess || !mediaId) {
        throw new Error("视频尚未上传成功，无法创建任务");
      }

      setUploadProgress(95);

      console.log(
        "creatTask:start " +
          JSON.stringify({
            mediaId: mediaId,
            task_name: videoName,
            robot_mode: robotModel,
          }),
      );
      const taskData = await creatTask({
        mediaId: mediaId,
        task_name: videoName,
        robot_mode: robotModel,
      });
      console.log("creatTask:success " + JSON.stringify(taskData));

      // 生成成功：提示已扣除的橡树果数量（数字用绿色展示）
      if (consumeAmount != null) {
        Toast.info({
          message: (
            <span>
              {t('createProject.consumedPrefix')}{" "}
              <span style={{ color: "#07C160" }}>{consumeAmount}</span> {t('createProject.consumedSuffix')}
            </span>
          ),
          duration: 2000,
        });
      }

      setUploadProgress(100);

      // 调用回调函数，将新的舞蹈项目信息传递给父组件
      if (onGenerate) {
        onGenerate({
          id: Date.now(), // 生成唯一ID
          name: videoName,
          model: robotModel,
          coverUrl: previewUrl,
          fileName: uploadedFile ? uploadedFile.name : "",
          fileType: uploadedFile ? uploadedFile.type : "",
          mediaId: mediaId,
          taskId: taskData ? taskData.task_id : null,
          status: taskData ? taskData.status : "生成中",
          progress: taskData ? taskData.progress : 0,
        });
      }

      // 重置状态
      setUploading(false);
      setUploadProgress(0);
      setUploadedFile(null);
      setPreviewUrl(null);
      setUploadUrl(null);
      setUploadCoverUrl(null);
      setMediaId(null);
      setVideoName("");
    } catch (error) {
      console.error("handleGenerateClick:failed " + error.message);
      setError(t('createProject.errorUploadFailed'));
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (!window.BoosterNativeBridge) {
    var BoosterNativeBridge = (function () {
      var isAndroid = /Android/i.test(navigator.userAgent);
      var isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      var callbackMap = new Map(); // 存储回调函数，key：回调ID
      let callbackId = 0;

      // 存储push消息回调函数
      let pushMessageCallback = null;

      /**
       * 前端调用 Native 方法（带返回值，Promise 版本）
       * @param methodName  方法名
       * @param params  参数
       * @returns  Promise 结果（resolve 成功值，reject 错误信息）
       */
      function callNativeWithResult(methodName, params = {}) {
        return new Promise((resolve, reject) => {
          if (!methodName) {
            reject(new Error("方法名不能为空"));
            return;
          }

          // 生成唯一回调ID，关联 resolve/reject
          const id = "cb_" + Date.now() + "_" + callbackId++;
          callbackMap.set(id, { resolve, reject });

          // 构造请求数据（包含回调ID，供 Native 回传结果）
          const requestData = {
            method: methodName,
            params: params,
            callbackId: id,
          };

          try {
            if (isAndroid) {
              // Android：调用 Native 注入的 window.BoosterNativeBridge（约定方法名 postMessage）
              if (window.NativeBridge && window.NativeBridge.postMessage) {
                console.log("Android 调用 Native 方法：", {
                  methodName,
                  params,
                });
                window.NativeBridge.postMessage(JSON.stringify(requestData));
              } else {
                reject(new Error("Android Native 未初始化"));
              }
            } else if (isIOS) {
              // iOS：调用 WKScriptMessageHandler（约定消息名 nativeWithResult）
              const handler = window.webkit?.messageHandlers?.nativeWithResult;
              if (handler?.postMessage) {
                handler.postMessage(requestData); // iOS 支持直接传对象
              } else {
                reject(new Error("iOS Native 未初始化"));
              }
            } else {
              // 模拟模式：在非 Native 环境中返回模拟数据
              if (methodName === "sendMessageToNative") {
                // 处理 Native 方法
                console.log("模拟调用 Native 方法：", { methodName, params });
                if (params.command === "close") {
                  resolve({ success: true, message: "模拟关闭成功" });
                } else if (params.command === "getRobotModel") {
                  resolve({
                    model: "Robot-X1",
                    version: "1.0.0",
                    manufacturer: "Robot Corp",
                  });
                } else {
                  resolve({ success: true, data: "模拟 Native 方法数据" });
                }
              } else if (methodName === "sendMessageToAgent") {
                // 处理 Agent 方法
                console.log("模拟调用 Agent 方法：", { methodName, params });
                if (params.command === "load_param_page") {
                  resolve({ params: { userId: "123", name: "张三", age: 25 } });
                } else if (params.command === "update_param") {
                  resolve({ success: true, message: "参数更新成功" });
                } else if (params.command === "getRobotModel") {
                  resolve({
                    model: "Robot-X1",
                    version: "1.0.0",
                    manufacturer: "Robot Corp",
                  });
                } else {
                  resolve({ success: true, data: "模拟 Agent 方法数据" });
                }
              } else {
                console.log("模拟调用方法：", { methodName, params });
                resolve({ success: true, data: "模拟数据" });
              }
            }
          } catch (e) {
            reject(e);
          }
        });
      }

      /**
       * Native 回传结果时调用的前端方法（Native 会主动调用）
       * @param response  Native 回传的数据（{ callbackId, data, error } ）
       */
      function onNativeResult(response) {
        try {
          const result =
            typeof response === "string" ? JSON.parse(response) : response;
          const { callbackId, data, error } = result;

          const { resolve, reject } = callbackMap.get(callbackId) || {};

          if (resolve && reject) {
            if (error) {
              reject(new Error(error.message || "Native 调用失败"));
            } else {
              resolve(data); // 返回值通过 resolve 传递给前端
            }
            callbackMap.delete(callbackId); // 移除已执行的回调，避免内存泄漏
          }
        } catch (e) {
          console.error("解析 Native 返回值失败：", e);
        }
      }

      /**
       * 注册push消息回调函数
       * @param onMessage 消息接收回调函数
       */
      function registerPushCallback(onMessage) {
        if (typeof onMessage === "function") {
          pushMessageCallback = onMessage;
          console.log("Push消息回调注册成功");
          return true;
        } else {
          console.warn("Push消息回调注册失败：参数不是函数");
          return false;
        }
      }

      /**
       * 取消注册push消息回调
       */
      function unregisterPushCallback() {
        pushMessageCallback = null;
        console.log("Push回调已取消注册");
        return true;
      }

      /**
       * Native调用此方法发送push消息到Web
       * @param messageData push消息数据
       */
      function onPushMessageFromNative(messageData) {
        try {
          const message =
            typeof messageData === "string"
              ? JSON.parse(messageData)
              : messageData;
          console.log("收到Native push消息:", message);

          if (pushMessageCallback) {
            pushMessageCallback(message);
          } else {
            console.warn("收到Native push消息，但未注册消息回调函数");
          }
        } catch (e) {
          console.error("解析Native push消息失败:", e);
        }
      }

      // 挂载到 window，供 Native 调用
      window.onNativeResult = onNativeResult;
      window.onPushMessageFromNative = onPushMessageFromNative;

      return {
        // Native调用方法
        close: () =>
          callNativeWithResult("sendMessageToNative", { command: "close" }),
        sendMessageToAgent: (generalParameters) =>
          callNativeWithResult("sendMessageToAgent", generalParameters),
        getParameters: () =>
          callNativeWithResult("sendMessageToAgent", {
            command: "load_param_page",
          }),
        saveParameters: (params) =>
          callNativeWithResult("sendMessageToAgent", {
            command: "update_param",
            parameters: params,
          }),

        // Push回调相关方法
        registerPushCallback: registerPushCallback,
        unregisterPushCallback: unregisterPushCallback,
        isPushCallbackRegistered: () => pushMessageCallback !== null,
      };
    })();

    window.BoosterNativeBridge = BoosterNativeBridge;
  }

  /**
   * 处理文件上传点击
   * 触发隐藏的文件输入框的点击事件
   */
  var handleUploadClick = () => {
    document.getElementById("file-input").click();
  };

/**
 * 处理文件选择
 * @param {Object} e - 事件对象
 * 1. 获取选择的文件
 * 2. 检查文件格式（只允许MP4/MOV/AVI/MKV）
 * 3. 设置上传的文件信息
 * 4. 生成文件预览（视频使用第一帧作为预览）
 * 5. 获取上传URL
 */
var handleFileChange = async (e) => {

  setUploadedFile(null);
  setPreviewUrl(null);
  setUploadUrl(null);
  setUploadCoverUrl(null);
  setUploading(false);
  setUploadProgress(0);

  // console.log("获取文件结果"+JSON.stringify(e))
  var file = e.target.files[0];
  if (!file) return;

  if (fileInputRef.current) {
    fileInputRef.current.value = "";
  }
  // 清除之前的错误信息
  if (error) {
    setError("");
  }

  // 获取文件扩展名（小写）
  var fileName = file.name.toLowerCase();
  var fileExt = fileName.split('.').pop();
  console.log("fileName:"+fileName)
  // 支持的视频格式数组
  var supportedFormats = ['mp4', 'mov', 'avi', 'mkv'];
  var supportedMimeTypes = {
    'mp4': 'video/mp4',
    'mov': 'video/quicktime',
    'avi': 'video/x-msvideo',
    'mkv': 'video/x-matroska'
  };

  // 检查是否是支持的格式
  var isSupportedExtension = supportedFormats.includes(fileExt);
  var isSupportedMimeType = false;
  var detectedFormat = null;

  // 检查MIME类型
  for (const [format, mime] of Object.entries(supportedMimeTypes)) {
    if (file.type === mime) {
      isSupportedMimeType = true;
      detectedFormat = format;
      break;
    }
  }

  // 如果是视频但不是支持的格式
  var isGenericVideo = file.type.startsWith("video/");

  // 格式验证：必须是支持的4种格式之一
  if (!isSupportedExtension && !isSupportedMimeType) {
    // 如果是其他视频格式
    if (isGenericVideo) {
      setError(t('createProject.errorUnsupportedVideo', { format: fileExt || file.type }));
    }
    // 如果是图片
    else if (file.type.startsWith("image/")) {
      setError(t('createProject.errorUnsupportedImage'));
    }
    // 其他所有文件类型
    else {
      setError(t('createProject.errorUnsupportedFile'));
    }

    // 重置文件选择
    e.target.value = '';
    setUploadedFile(null);
    setPreviewUrl(null);
    return;
  }

  // 确定检测到的格式
  var format = detectedFormat || fileExt;
  console.log(`检测到${format.toUpperCase()}格式文件: ${fileName}`);

  // 时长校验：仅允许大于15秒且小于60秒的视频
  var durationSeconds = await getMediaDurationSeconds(file);
  if (durationSeconds < 3 || durationSeconds >= 90) {
    setError("");
    Toast.info(t('createProject.errorDuration'));
    e.target.value = '';
    setUploadedFile(null);
    setPreviewUrl(null);
    return;
  }

  // 设置上传的文件信息
  setUploadedFile(file);
  // 记录视频时长，供确认弹框展示
  setVideoDuration(durationSeconds);

// 尝试生成视频封面预览
  var coverUrl;


  try {
    coverUrl = await getVideoCoverDataUrl(file);
    // console.log("coverUrl 类型:", typeof coverUrl, coverUrl); // 打印 coverUrl 的类型和值

    // 额外检查：如果 coverUrl 是 data URL，检查是否是黑帧
    if (coverUrl && coverUrl.startsWith('data:image')) {
      // 这里可以添加一个额外的检查，但为了简化，我们直接信任 getVideoCoverDataUrl 的逻辑
      // 如果 getVideoCoverDataUrl 已经做了黑帧检测，这里就不需要重复了
      setPreviewUrl(coverUrl);

    } else {
      // 如果 coverUrl 不是有效的图片 URL，使用默认图片
      console.warn("封面URL无效，使用默认封面");
      setPreviewUrl(defaultCoverImage);
    }
  } catch (e) {
    console.error("get video cover failed:", e?.message || e);

    // 特殊处理某些格式
    if (format === 'mkv' || format === 'avi') {
      console.warn(`${format.toUpperCase()}格式可能无法生成预览，使用默认封面`);
      // 使用默认封面
      setPreviewUrl(defaultCoverImage);
      if (!error) {
        setError(t('createProject.errorPreviewUnavailable', { format: format.toUpperCase() }));
      }
    } else {
      console.warn("视频封面生成失败，使用默认封面");
      setPreviewUrl(defaultCoverImage);
    }
  }
  var video;
  if(videoName!=null &&videoName.length>1){
    video = videoName
  }else{
    if (fileName.length>20){
      video = fileName.substring(fileName.length - 10);
    }else{
      video = fileName
    }
  }

  console.log("mediaName video:"+video)
  // 处理上传流程
  // 只使用输入框里的舞蹈名称，不再回退到文件名
  var mediaName =  video;
  console.log("mediaName:"+mediaName)

  setUploading(true);  // 开始上传流程
  setUploadProgress(0);


  try {
    var urlsData = await getUploadUrls(mediaName);
    if (urlsData) {
      console.log("getUploadUrls:success " + JSON.stringify(urlsData));
      setMediaId(urlsData.media_id);
      setUploadSuccess(false);
      // 获取视频url,上传视频
      try {
        var fileUploadSuccess = await handleFileUpload(
            urlsData.upload_url,
            file,
        );
        if (!fileUploadSuccess) {  // 如果上传失败
          console.error("handleFileUpload:failed 上传失败");
          setError(t('createProject.errorVideoUploadFailed'));
          setUploadSuccess(false);
          setMediaId(null);  // 重置mediaId
          setUploadUrl(null);
          setUploadCoverUrl(null);
          setUploading(false);
          return;  // 关键：立即返回，不再执行后续代码
        }
        
        console.log("handleFileUpload:success");


        if (fileUploadSuccess) {
          console.log(
              "handleFileUpload:success " +
              JSON.stringify(fileUploadSuccess),
          );

          // 视频文件已真正上传成功，立即标记，允许创建任务
          setUploadSuccess(true);

          // 如果有封面，上传封面（封面/元数据失败不阻断生成）
          if (coverUrl || previewUrl) { // 修改这里，也检查 previewUrl
            try {
              var coverUrlToUpload = coverUrl || previewUrl || defaultCoverImage; // 优先封面，其次预览，最后默认图
              var coverUploadSuccess = await uploadCover(
                  coverUrlToUpload, // 这里会自动处理默认图片
                  urlsData.upload_cover_url,
              );
              if (!coverUploadSuccess && coverUrlToUpload !== defaultCoverImage) {
                console.warn("封面上传失败，回退上传默认封面");
                coverUploadSuccess = await uploadCover(
                    defaultCoverImage,
                    urlsData.upload_cover_url,
                );
              }
              console.log(
                  "uploadCover:success " + JSON.stringify(coverUploadSuccess),
              );

              // 更新媒体状态
              try {
                var updateUlrStatus = await updateUrl(
                    urlsData.media_id,
                    mediaName,
                    file
                );
                console.log(
                    "updateUrl:success " + JSON.stringify(updateUlrStatus),
                );
              } catch (error) {
                console.error("updateUrl:failed " + error.message);
              }
            } catch (error) {
              console.error("uploadCover:failed " + error.message);
            }
          } else {
            console.log("无封面图片，跳过封面上传");
          }

          setUploading(false);
        } else {
          console.error("handleFileUpload:failed 上传失败");
          setError(t('createProject.errorVideoUploadFailed'));          
return
;
        }
      } catch (error) {
        console.error("handleFileUpload:failed " + error.message);
        setError(t('createProject.errorVideoUploadError', { message: error.message }));
        setUploadSuccess(false);
        setUploading(false);
         
return
;
      }
    } else {
      console.error("getUploadUrls:failed 获取上传链接失败");
      setError(t('createProject.errorGetUploadUrlFailed'));
      setUploading(false);
       
return
;
    }
  } catch (error) {
    console.error("getUploadUrls:failed " + error.message);
    setError(t('createProject.errorGetUploadUrlError', { message: error.message }));
    setUploading(false);
     
return
;
  }
};
  /**
   * 处理删除文件
   * 重置所有与上传相关的状态
   */
  var handleDeleteFile = () => {
    setUploadedFile(null);
    setPreviewUrl(null);
    setUploadUrl(null);
    setUploadCoverUrl(null);
    setUploadSuccess(false);
    setVideoDuration(null);
    // setMediaId(null);
    setUploading(false);
    setUploadProgress(0);
  };

  /**
   * 获取上传URL
   * @param {string} mediaName - 媒体名称
   * @returns {Promise<Object|null>} - 返回上传URL信息或null
   * 1. 发送POST请求到 /cmtp/api/v1/media/upload-url 接口获取上传URL
   * 2. 处理响应数据，设置上传URL、封面上传URL和媒体ID
   * 3. 处理错误情况，设置错误信息
   */
  var getUploadUrls = async (mediaName) => {
    try {
      // 发送真实的API请求
      var data = await api.post(
        "/api/v1/media/upload-url",
        {
          media_name: mediaName, // 传递媒体名称参数
          user_id: localStorage.getItem("user_id"),
        },
      );

      console.log("Response data:", data);

      // 响应已由 api.js 的 instance 拦截器统一解包，这里 data 即业务数据
      // 设置上传URL、封面上传URL和媒体ID
      setUploadUrl(data.upload_url);
      setUploadCoverUrl(data.upload_cover_url);
      setMediaId(data.media_id);

      console.log("mediaId:" + data.media_id);

      return data; // 返回上传URL信息
    } catch (error) {
      console.error("Error getting upload URLs:", error);

      // 处理不同类型的错误
      if (error.response) {
        if (error.response.status === 422) {
          // 处理422错误（验证错误）
          var errorData = error.response.data;
          var errorMessage = errorData.detail
            .map((detail) => detail.msg)
            .join(", ");
          setError(errorMessage);
        } else if (error.response.status === 404) {
          // 处理404错误（资源不存在）
          setError(t('createProject.errorResourceNotFound'));
        } else {
          // 处理其他HTTP错误
          setError(t('createProject.errorGetUploadUrlFailed'));
        }
      } else {
        // 处理网络异常
        setError(t('createProject.errorNetwork'));
      }

      return null; // 返回null表示获取失败
    }
  };

  /**
   * 处理文件上传
   * @param {string} url - 上传URL
   * @param {Object} file - 要上传的文件
   * @returns {Promise<boolean>} - 返回上传是否成功
   * 1. 确定文件类型
   * 2. 使用 axios 发送 PUT 请求上传文件
   * 3. 监听上传进度并更新状态
   * 4. 处理上传成功和失败的情况
   */
var handleFileUpload = async (url, file) => {
  // 根据文件扩展名确定Content-Type
  let fileType = file.type;
  const fileName = file.name.toLowerCase();
  const fileExt = fileName.split('.').pop();

  // 支持的格式对应的MIME类型
  const mimeTypes = {
    'mp4': 'video/mp4',
    'mov': 'video/quicktime',
    'avi': 'video/x-msvideo',
    'mkv': 'video/x-matroska'
  };

  // 如果浏览器没有正确识别MIME类型，手动设置
  if (!fileType || fileType === 'application/octet-stream') {
    if (mimeTypes[fileExt]) {
      fileType = mimeTypes[fileExt];
    } else {
      fileType = 'video/mp4'; // 默认
    }
  }

  // console.log(`上传文件类型: ${fileType}, 文件名: ${fileName}, 格式: ${fileExt}`);

  try {
    // 用原生 XHR 直传：不设置 Content-Type。
    // axios 对 PUT 默认会补 Content-Type: application/x-www-form-urlencoded，
    // 会被阿里云 V1 预签名 URL 算进签名 → SignatureDoesNotMatch。
    // new Blob([file]) 同时清掉 File 自带的 type，避免浏览器自动补 Content-Type。
    var response = await new Promise((resolve, reject) => {
      var xhr = new XMLHttpRequest();
      xhr.open("PUT", url, true);
      xhr.upload.onprogress = (progressEvent) => {
        // 计算并更新上传进度
        if (progressEvent.total) {
          var progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          setUploadProgress(progress); // 更新上传进度状态
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr);
        } else {
          console.error("OSS 响应状态码:", xhr.status);
          console.error("OSS 响应内容:", xhr.responseText);
          reject(new Error("OSS 上传失败: " + xhr.status));
        }
      };
      xhr.onerror = () => reject(new Error("OSS 上传网络错误"));
      xhr.send(new Blob([file]));
    });

    console.log("File uploaded successfully:", response);
    return true; // 上传成功


  } catch (error) {
    console.error("File upload error:", error);
    // 打出 OSS 返回的状态码与响应体（XML），便于定位 403 具体原因
    if (error.response) {
      console.error("OSS 响应状态码:", error.response.status);
      console.error("OSS 响应内容:", error.response.data);
    }
    return false; // 上传失败
  }
};

  /**
   * 更新媒体状态
   * @param {string} mediaId - 媒体ID
   * @param {string} videoName - 视频名称
   * @returns {Promise<Object|null>} - 返回更新结果或null
   * 1. 获取视频时长
   * 2. 调用 updateMediaMetadata 函数更新媒体元数据
   */
  var updateUrl = async (mediaId, videoName,file) => {
    try {
      // 获取视频时长
      var duration = await getMediaDurationSeconds(file);
      console.log("updateUrl:duration " + duration);
      console.log(
        "updateUrl:params " +
          JSON.stringify({ mediaId: mediaId, videoName: videoName }),
      );

      // 调用 updateMediaMetadata 函数更新媒体元数据
      console.log(
        "updateMediaMetadata:start " +
          JSON.stringify({
            mediaId: mediaId,
            mediaName: videoName,
            duration: duration,
            status: "success",
          }),
      );
      var updateMediaMeta = await updateMediaMetadata({
        mediaId: mediaId,
        mediaName: videoName,
        duration: duration,
        status: "success",
      });
      console.log(
        "updateMediaMetadata:success " + JSON.stringify(updateMediaMeta),
      );
      return updateMediaMeta;
    } catch (e) {
      // 这里不阻断主流程：即使元数据写入失败，也允许用户继续上传封面/生成项目（后端可后续补偿或人工修复）
      console.error("updateMediaMetadata:failed " + e.message);
      return null;
    }
  };

  /**
   * 上传封面图片
   * @param {string} coverUrl - 封面图片URL（base64格式）
   * @param {string} uploadCoverUrl - 封面上传URL
   * @returns {Promise<boolean>} - 返回上传是否成功
   * 1. 将base64转换为Blob
   * 2. 创建一个新的axios实例，设置超时时间为60秒
   * 3. 发送PUT请求上传封面图片
   * 4. 处理错误情况，设置错误信息
   */
  var uploadCover = async (coverUrl, uploadCoverUrl) => {
    try {
      // 将base64转换为Blob
      var response = await fetch(coverUrl);
      var blob = await response.blob();

      // 用原生 XHR 直传，不设置 Content-Type（与视频上传同理，避免阿里云签名不匹配）
      await new Promise((resolve, reject) => {
        var xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadCoverUrl, true);
        xhr.timeout = 60000; // 上传超时 60 秒
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr);
          } else {
            console.error("OSS 封面响应状态码:", xhr.status);
            console.error("OSS 封面响应内容:", xhr.responseText);
            reject(new Error("OSS 封面上传失败: " + xhr.status));
          }
        };
        xhr.onerror = () => reject(new Error("OSS 封面上传网络错误"));
        xhr.ontimeout = () => reject(new Error("OSS 封面上传超时"));
        xhr.send(new Blob([blob]));
      });

      return true; // 上传成功
    } catch (error) {
      console.error("Error uploading cover:", error);
      setError(t('createProject.errorCoverUploadFailed'));
      return false; // 上传失败
    }
  };

  return (
    <div className="create-form">
      <span className="form-title">{t('createProject.title')}</span>
      <div
        className="upload-area"
        onClick={handleUploadClick}
        style={{ cursor: "pointer" }}
      >
        {uploadedFile ? (
          // 显示上传的文件名称
          <div className="file-preview">
            <div className="file-icon-container">
              {previewUrl ? (
                // 显示图片或视频封面预览
                <img
                  src={previewUrl}
                  alt="文件预览"
                  className="file-preview-image"
                />
              ) : (
                // 显示默认的文件图标
                <div className="file-icon">
                  {uploadedFile.type.startsWith("image/")
                    ? "🖼️"
                    : uploadedFile.type.startsWith("video/")
                      ? "🎥"
                      : "📄"}
                </div>
              )}
              <button
                className="delete-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteFile();
                }}
              >
                ×
              </button>
            </div>
            <div className="file-name">{uploadedFile.name}</div>
            <div className="upload-hint">{t('createProject.uploadTip')}</div>
          </div>
        ) : (
          // 显示默认的上传提示
          <>
            <img src={UploadIcon} className="upload-icon" alt="上传" />
            <div className="upload-text">{t('createProject.uploadVideo')}</div>
            <div className="upload-hint">{t('createProject.uploadTip')}</div>
          </>
        )}
        <input
          type="file"
          id="file-input"
          ref={fileInputRef}
          accept=".mp4,.mov,.avi,.mkv,video/mp4,video/quicktime,video/x-msvideo,video/x-matroska"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
      </div>
      <div className="form-fields">
        <div className="form-field">
          <label>
            {t('createProject.danceName')} <span style={{ color: "red" }}>*</span>
          </label>
          <input
              ref={videoNameRef}
              // 修改这里：使用 onClick 替代 onMouseDown
              onClick={() => {
                const input = videoNameRef?.current;
                if (!input || input !== document.activeElement) {
                  // 强制聚焦并尝试触发软键盘
                  input?.focus();
                  // 移动端延迟一下确保键盘能弹起
                  setTimeout(() => {
                    input?.focus();
                  }, 100);
                }
              }}
              // 保留触摸事件处理
              onTouchEnd={(e) => {
                e.preventDefault(); // 阻止默认行为
                const input = videoNameRef?.current;
                if (!input) return;
                input.focus();
                // 移动端特别处理：延迟聚焦确保键盘弹起
                setTimeout(() => {
                  input.focus();
                  // 尝试触发虚拟键盘
                  input.setAttribute('inputmode', 'text');
                }, 50);
              }}
              type="text"
              placeholder={t('createProject.danceNamePlaceholder')}
              value={videoName}
              onChange={handleVideoNameChange}
              className={error ? "error" : ""}
              // 添加移动端优化属性
              inputMode="text"
              enterKeyHint="done"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
          />

          {error && <div className="error-message">{error}</div>}
        </div>
        <div className="form-field">
          <label>{t('createProject.robotModel')}</label>
          <select value={robotModel} onChange={(e) => setRobotModel(e.target.value)}>
            {/* <option value="booster_t1">Booster T1</option> */}
            <option value="booster_k1">booster_k1</option>
          </select>
        </div>
      </div>
      <div

        style={{
          marginTop: "4px",
          marginBottom: "18px",
          color: "rgba(157, 163, 175, 1)",

          lineHeight: "24px",

        }}
      >
        <div style={{ marginBottom: "6px", color: "#252B37", fontWeight: 600,fontSize: "14px", }}>
          {t('createProject.instructions')}
        </div>
        {uploadInstructions.map((item) => (
          <div className="upload-hint"
               key={item}>{item}</div>
        ))}
        <div style={{ marginTop: "4px" ,fontSize: "14px"}}>{t('createProject.note')}</div>
      </div>

      {/* 上传进度条已隐藏：仅保留上传状态逻辑 */}
      <button
        className="generate-button"
        onClick={handleGenerateButtonClick}
        disabled={uploading}
      >
        {t('createProject.generateNow')}
      </button>

      {/* 生成舞蹈确认弹框：选择生成方式 */}
      {showGenerateConfirmDialog && (
        <div className="dialog-overlay">
          <div className="generate-confirm-dialog">
            <h4 className="dialog-title">{t('createProject.generateDance')}</h4>
          <p className="dialog-message">{t('generateDialog.confirmInfo')}</p>

       
<div className="card-container">
  <div className="card-item">
    <span className="label">{t('generateDialog.danceName')}</span>
    <span className="value">{videoName || "—"}</span>
  </div>

  <div className="card-item">
    <span className="label">{t('generateDialog.robotModel')}</span>
    <span className="value">{robotModel}</span>
  </div>

  <div className="card-item">
    <span className="label">{t('generateDialog.duration')}</span>
    <span className="value highlight">{videoDuration != null ? `${videoDuration}${t('generateDialog.durationUnit')}` : "—"}</span>
  </div>
</div>

            <div
              className={`generate-option ${generateType === "full" ? "selected" : ""}`}
              onClick={() => setGenerateType("full")}
            >
             
              <span className="generate-option-text">{firstProduct ? t('generateDialog.generateFull') : "—"}</span>
            </div>
            <p className="generate-buju">
               <span className="buju-text">{t('generateDialog.currentBalance')}</span>
               <span className="buju-text bj-jc">{acornBalance ?? "—"}{t('common.acornUnit')}</span>
            </p>

             <h4 className="generate-buju">
               <span>{t('generateDialog.willConsume')}</span>
               <span className="generate-hs">{consumeAmount ?? "—"}{t('common.acornUnit')}</span>
            </h4>
            <div className="dialog-buttons">
              <button
                className="dialog-button cancel"
                onClick={() => setShowGenerateConfirmDialog(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                className="dialog-button confirm"
                onClick={proceedGenerate}
              >
                {t('generateDialog.confirmGenerate')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 立即生成确认弹框（复用 Home 的 confirm-dialog 样式） */}
      {showGenerateDialog && (
        <div className="dialog-overlay">
          <div className="PayDialog" role="dialog" aria-modal="true">
            {/* 根据支付状态显示不同内容 */}
            {paymentStatus === PAYMENT_STATUS.CONFIRMING && (
              <>
                <div className="PayTitle">{t('payment.title')}</div>

                <div className="PayCard">
                  <div className="PayCardRow">
                    <div className="PayCardLeft">
                      <div className="PayCardLabel">{t('payment.orderInfo')}</div>
                      <div className="PayCardSub">{t('payment.totalDesc')}</div>
                    </div>
                    <div className="PayCardRight">
                      <div className="PayCardName">{videoName || "—"}</div>
                      <div className="PayCardAmount">¥{Number(orderInfo?.amount || 0).toFixed(2)}</div>
                    </div>
                  </div>
                  {!!orderInfo?.productDesc && (
                    <div
                      style={{
                        marginTop: "8px",
                        color: "#667085",
                        fontSize: "12px",
                        lineHeight: "18px",
                      }}
                    >
                      {orderInfo.productDesc}
                    </div>
                  )}
                </div>

                <div className="PaySection">
                  <div className="PaySectionTitle">{t('payment.payMethod')}</div>
                  <div className="PayMethods">
                    <button
                      type="button"
                      className={`PayMethod PayMethod--wechat ${payMethod === "wechat" ? "is-active" : ""}`}
                      onClick={() => setPayMethod("wechat")}
                      disabled
                    >
                      {t('payment.wechat')}
                    </button>
                    <button
                      type="button"
                      className={`PayMethod PayMethod--alipay ${payMethod === "alipay" ? "is-active" : ""}`}
                      onClick={() => setPayMethod("alipay")}
                    >
                      {t('payment.alipay')}
                    </button>
                    <button
                      type="button"
                      className={`PayMethod PayMethod--unionpay ${payMethod === "unionpay" ? "is-active" : ""}`}
                      onClick={() => setPayMethod("unionpay")}
                      disabled
                    >
                      {t('payment.unionpay')}
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  className="PayConfirmButton"
                  onClick={handlePaymentProcess}
                  disabled={uploading}
                >
                  {t('payment.confirmPay')}
                </button>

                <button
                  type="button"
                  className="PayCancelButton"
                  onClick={handleCancelPayment}
                  style={{
                    marginTop: "12px",
                    background: "transparent",
                    color: "#666",
                    border: "1px solid #ddd"
                  }}
                >
                  {t('common.cancel')}
                </button>
              </>
            )}

            {/* 支付中状态 */}
            {paymentStatus === PAYMENT_STATUS.PROCESSING && (
              <div className="payment-processing-state">
                <div className="payment-spinner"></div>
                <div className="payment-processing-text">{t('payment.processingText')}</div>
                <div className="payment-hint">
                  {payErrorMsg || t('payment.processingHint')}
                </div>
              </div>
            )}

            {/* 支付成功状态 */}
            {paymentStatus === PAYMENT_STATUS.SUCCESS && (
              <div className="PaySuccessCard">
                <div className="PaySuccessTitle">{t('payment.confirmPayment')}</div>
                <div className="PaySuccessAmount">¥{Number(orderInfo?.amount || 0).toFixed(2)}</div>
                <div className="PaySuccessRows">
                  <div className="PaySuccessRow">
                    <span>{t('payment.orderInfo')}</span>
                    <span>{videoName || "—"}</span>
                  </div>
                  <div className="PaySuccessRow">
                    <span>{t('payment.payMethodLabel')}</span>
                    <span>{t('payment.alipay')}</span>
                  </div>
                </div>
                <div className="PaySuccessIcon">✓</div>
                <div className="PaySuccessText">{t('payment.paySuccess')}</div>
                <div className="PaySuccessHint">{t('payment.paySuccessHint')}</div>
              </div>
            )}

            {/* 支付失败状态 */}
            {paymentStatus === PAYMENT_STATUS.FAIL && (
              <div className="payment-fail-state">
                <div className="payment-fail-icon">❌</div>
                <div className="payment-fail-text">{t('payment.payFailed')}</div>
                <div className="payment-fail-hint">{payErrorMsg}</div>
                <div className="payment-fail-buttons">
                  <button
                    type="button"
                    className="PayRetryButton"
                    onClick={handlePaymentProcess}
                  >
                    {t('payment.repay')}
                  </button>
                  <button
                    type="button"
                    className="PayCancelButton"
                    onClick={handleCancelPayment}
                    style={{
                      background: "transparent",
                      color: "#666",
                      border: "1px solid #ddd"
                    }}
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showIosTipDialog && (
        <div className="dialog-overlay">
          <div className="delete-dialog">
            <h3 className="dialog-title">{t('payment.contactService')}</h3>
            <div className="dialog-buttons" style={{ justifyContent: "center" }}>
              <button
                className="dialog-button confirm"
                onClick={() => setShowIosTipDialog(false)}
              >
                {t('payment.gotIt')}
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        ref={paymentFormRef}
        style={{ display: "none" }}
        aria-hidden="true"
      />


    </div>
  );
}